import React, { useCallback, useEffect, useState } from 'react';
import { breakpoints } from '@naf/theme';
import { AdvancedImage } from '@cloudinary/react';
import { ArticleHeader } from '@naf-simba/sections-headers';
import { BreadcrumbData } from 'use-react-router-breadcrumbs';
import { GridCol } from '@naf/grid';
import styled from '@emotion/styled';
import { nafColor } from '@nafcore/theme';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { useWindowResize } from '@hooks/Window/useWindowResize';
import { HeroWithTitleAndWideImageType } from '@pages/DynamicApp/Hero/HeroWithTitleAndWideImage';
import BreadCrumb from '@components/breadcrumb/BreadCrumb';
import { ThemeVariants } from '@appTypes/themes';
import { FullScreenHeaderWrapper } from './styles';

export const TitleAndWideImageHero = ({
  hero: { header, image, mobileImage, ingress, overlay, halfScreen },
  breadCrumbs,
  themeType,
}: {
  hero: HeroWithTitleAndWideImageType;
  breadCrumbs?: BreadcrumbData[];
  themeType?: string;
}) => {
  const { getResponsiveImage } = useCloudinary();
  const imagePublicId = image?.public_id || image?.publicId;
  const mobileImagePublicId = mobileImage?.public_id || mobileImage?.publicId;

  const [mainImage, setMainImage] = useState(
    getResponsiveImage(imagePublicId, {
      width: 1120,
      focusMethod: image?.focus,
    }),
  );

  const setResponsiveImageForJustSuper = useCallback(() => {
    const heroImage = window.innerWidth <= parseInt(breakpoints.s, 10) && mobileImagePublicId ? mobileImage : image;
    const heroImagePublicId = heroImage?.public_id || heroImage?.publicId;

    if (heroImagePublicId && typeof window !== 'undefined') {
      const isMobile = window.innerWidth <= parseInt(breakpoints.m, 10);
      const deviceWidth = window.innerWidth;
      const deviceHeight = window.screen.height;
      const minWidth = 310;
      const minHeight = 480;

      if (isMobile) {
        // Resize image based on device width, using a fixed height to avoid resizing issues on scroll.
        setMainImage(
          getResponsiveImage(heroImagePublicId, {
            width: Math.max(deviceWidth, minWidth),
            height: Math.max(deviceHeight, minHeight),
            focusMethod: heroImage?.focus,
          }),
        );
      } else {
        // Use full width for larger screens as already implemented.
        setMainImage(
          getResponsiveImage(heroImagePublicId, {
            width: deviceWidth,
            focusMethod: heroImage?.focus,
          }),
        );
      }
    }
  }, [mobileImagePublicId, mobileImage, image, getResponsiveImage]);

  // Function to handle the default logic for other themes - as already implemented.
  const setResponsiveImageDefault = useCallback(() => {
    const heroImage = window.innerWidth <= parseInt(breakpoints.s, 10) && mobileImagePublicId ? mobileImage : image;
    const heroImagePublicId = heroImage?.public_id || heroImage?.publicId;

    if (heroImagePublicId && typeof window !== 'undefined') {
      if (window.innerWidth <= parseInt(breakpoints.m, 10)) {
        const height = window.innerHeight;
        setMainImage(
          getResponsiveImage(heroImagePublicId, {
            height,
            focusMethod: mobileImage?.focus,
          }),
        );
      } else {
        const width = window.innerWidth;
        setMainImage(
          getResponsiveImage(heroImagePublicId, {
            width,
            focusMethod: image?.focus,
          }),
        );
      }
    }
  }, [mobileImagePublicId, mobileImage, image, getResponsiveImage]);

  // Determine which logic to use based on the theme - currently only themeJustSuper.
  const setResponsiveImage = useCallback(() => {
    if (themeType === ThemeVariants.JustSuper) {
      setResponsiveImageForJustSuper();
    } else {
      setResponsiveImageDefault();
    }
  }, [themeType, setResponsiveImageForJustSuper, setResponsiveImageDefault]);

  useEffect(() => {
    setResponsiveImage();
  }, [setResponsiveImage]);

  useWindowResize(() => {
    setResponsiveImage();
  });

  if (mainImage) {
    return (
      <FullScreenHeaderWrapper>
        {breadCrumbs && breadCrumbs.length > 1 ? (
          <BreadcrumbWrapper>
            <GridCol>
              <BreadCrumb />
            </GridCol>
          </BreadcrumbWrapper>
        ) : null}
        <ArticleHeader
          imageOrVideo={<AdvancedImage cldImg={mainImage} alt={image.alt || image.altText || image.caption} />}
          ingress={ingress}
          title={header}
          headingVariant="display"
          nafBranded={false}
          overlay={overlay}
          halfScreen={halfScreen}
        />
      </FullScreenHeaderWrapper>
    );
  }
  return <ArticleHeader nafBranded={false} ingress={ingress} title={header} halfScreen={halfScreen} />;
};

const BreadcrumbWrapper = styled.div`
  position: absolute;
  margin: 0 auto;
  padding: 0 48px;
  top: 90px;
  max-width: 1120px;
  right: 0;
  left: 0;
  z-index: 10;
  display: flex;
  @media (max-width: ${breakpoints.m}) {
    top: 60px;
    padding: 0 24px;
  }
  * {
    color: ${nafColor.signature.white} !important;
    text-decoration-color: ${nafColor.signature.white};
  }
`;
